import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, ShieldCheck, Zap, Tag } from "lucide-react";
import ForgotPasswordDialog from "@/components/ForgotPasswordDialog";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import { useSEO } from "@/hooks/useSEO";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedNavigate, useLocalizedPath } from "@/hooks/useLocalizedNavigate";
import { DirectionalArrow } from "@/components/DirectionalArrow";

const Connexion = () => {
  const { t, locale } = useLanguage("auth");
  const lNavigate = useLocalizedNavigate();
  const lp = useLocalizedPath();

  useSEO({ noindex: true });

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect");

  const benefits = [
    { icon: ShieldCheck, text: t("benefits.verified") },
    { icon: Zap,         text: t("benefits.fast_contact") },
    { icon: Tag,         text: t("benefits.free") },
  ];

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { data: authData, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      toast({
        title: t("connexion.error_generic"),
        description: error.message === "Invalid login credentials"
          ? t("connexion.error_invalid")
          : error.message === "Email not confirmed"
          ? t("connexion.error_not_confirmed")
          : error.message,
        variant: "destructive",
      });
    } else {
      toast({ title: t("connexion.success_title"), description: t("connexion.success_desc") });
      if (redirectTo) {
        lNavigate(redirectTo);
      } else if (authData.user) {
        const { data: roles } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", authData.user.id);
        const isArtisan = roles?.some(r => r.role === "artisan_entreprise" || r.role === "artisan_bricoleur");
        lNavigate(isArtisan ? "/dashboard" : "/mes-annonces");
      } else {
        lNavigate("/");
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left decoration panel — desktop only */}
      <div className="hidden lg:flex lg:w-2/5 xl:w-1/2 auth-decoration flex-col justify-between p-10 xl:p-14">
        <Link to={lp("/")} aria-label="Hirafi — Accueil">
          <img
            src={locale === "ar" ? "/logo-ar-bw.png" : "/logo-fr-bw.png"}
            alt="Hirafi"
            className="h-8 w-auto brightness-0 invert"
          />
        </Link>

        <div className="space-y-8">
          <div className="space-y-3">
            <h2 className="font-display text-3xl xl:text-4xl font-bold text-white leading-tight">
              {t("connexion.decoration_title")}
            </h2>
            <p className="text-white/70 text-lg leading-relaxed">
              {t("connexion.decoration_subtitle")}
            </p>
          </div>
          <ul className="space-y-4">
            {benefits.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3 text-white/85">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/15">
                  <Icon className="h-4 w-4 text-white" aria-hidden="true" />
                </div>
                <span className="text-sm font-medium">{text}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="text-white/40 text-xs">© 2026 Hirafi · Tous droits réservés</p>
      </div>

      {/* Right form panel */}
      <div className="flex flex-1 flex-col px-5 py-6 sm:px-8 lg:px-12 xl:px-16">

        <div className="flex items-center justify-between lg:justify-end mb-6 lg:mb-4">
          <Link to={lp("/")} className="lg:hidden" aria-label="Hirafi — Accueil">
            <img
              src={locale === "ar" ? "/logo-ar.png" : "/logo-fr.png"}
              alt="Hirafi"
              className="h-8 w-auto"
            />
          </Link>

          <Link
            to={lp("/")}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
            aria-label={t("connexion.home")}
          >
            <DirectionalArrow className="h-3.5 w-3.5 rotate-180" aria-hidden="true" />
            {t("connexion.home")}
          </Link>
        </div>

        <div className="flex flex-1 flex-col justify-center">
          <div className="w-full max-w-md mx-auto">
            <div className="mb-8">
              <h1 className="font-display text-2xl font-bold md:text-3xl">{t("connexion.title")}</h1>
              <p className="mt-1.5 text-muted-foreground">{t("connexion.subtitle_long")}</p>
            </div>

            <div className="space-y-4">
              <GoogleAuthButton label={t("google.sign_in")} />
              <div className="relative flex items-center gap-3">
                <div className="flex-1 border-t border-border" />
                <span className="text-xs text-muted-foreground">{t("connexion.or")}</span>
                <div className="flex-1 border-t border-border" />
              </div>
            </div>

            <form onSubmit={handleLogin} className="space-y-5 mt-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="font-display font-semibold text-sm">{t("connexion.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="votre@email.dz"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="h-11"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="font-display font-semibold text-sm">{t("connexion.password")}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="h-11"
                />
              </div>

              <Button variant="hero" size="xl" className="w-full mt-2 gap-2" type="submit" disabled={loading}>
                <LogIn className="h-5 w-5" />
                {loading ? t("connexion.loading") : t("connexion.submit")}
              </Button>
            </form>

            <div className="mt-6 space-y-2 text-center text-sm text-muted-foreground border-t pt-6">
              <p>
                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  className="text-primary hover:underline underline-offset-4"
                >
                  {t("connexion.forgot_password")}
                </button>
              </p>
              <p>
                {t("connexion.no_account")}{" "}
                <Link
                  to={redirectTo ? lp(`/inscription-client?redirect=${encodeURIComponent(redirectTo)}`) : lp("/inscription-client")}
                  className="font-semibold text-primary hover:underline underline-offset-4"
                >
                  {t("connexion.register_client")}
                </Link>
              </p>
              <p>
                {t("connexion.artisan_q")}{" "}
                <Link to={lp("/inscription")} className="font-semibold text-primary hover:underline underline-offset-4">
                  {t("connexion.artisan_register")}
                </Link>
              </p>

              <p className="pt-3 border-t border-border/40">
                <Link
                  to={redirectTo || lp("/")}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                >
                  {t("connexion.continue_without")}
                  <DirectionalArrow className="h-3 w-3" aria-hidden="true" />
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <ForgotPasswordDialog
        open={forgotOpen}
        onOpenChange={setForgotOpen}
        defaultEmail={email}
      />
    </div>
  );
};

export default Connexion;
